import { GraphicsService } from "appworks/graphics/graphics-service";
import { Sprite } from "appworks/graphics/pixi/sprite";
import { gameState } from "appworks/model/game-state";
import { CurrencyService } from "appworks/services/currency/currency-service";
import { Services } from "appworks/services/services";
import { SoundEvent } from "appworks/services/sound/sound-events";
import { SoundService } from "appworks/services/sound/sound-service";
import { TransactionService } from "appworks/services/transaction/transaction-service";
import { UIFlag, uiFlags } from "appworks/ui/flags/ui-flags";
import { Contract } from "appworks/utils/contracts/contract";
import { Parallel } from "appworks/utils/contracts/parallel";
import { Sequence } from "appworks/utils/contracts/sequence";
import { Easing, Tween } from "appworks/utils/tween";
import { gameLayers } from "game-layers";
import { FreespinRecord } from "slotworks/model/gameplay/records/freespin-record";
import { BonusResult } from "slotworks/model/gameplay/records/results/bonus-result";
import { SymbolWinResult } from "slotworks/model/gameplay/records/results/symbol-win-result";
import { LoopWinsState } from "slotworks/state-machine/standard/states/loop-wins-state";

export class FowlSlotLoopWinsState extends LoopWinsState {
    protected totalWinValue: number;
    protected winBoxes: Sprite[] = [];

    public onEnter(cascadeSkip?: boolean) {
        if (cascadeSkip) {
            this.onSkip();
        } else {
            this.totalWinValue = this.getTotalSlotWin(false);
            this.updateWinText();

            super.onEnter();
        }
    }

    public startLoop(): Contract<void> {
        if (gameState.currentRecordHasSymbolWinResults()) {
            return super.startLoop();
        } else {
            return this.cancelGroup.timeoutContract(250);
        }
    }

    public onExit(): void {
        uiFlags.set(UIFlag.NO_SKIP, true);

        this.winBoxes.forEach(box => box.destroy());
        this.winBoxes = [];

        this.totalWinValue = this.getTotalSlotWin(true) - this.getUnplayedBonusWin();
        this.updateWinText();

        const gameplay = gameState.getCurrentGame();
        Services.get(TransactionService).setTotalWin(gameplay.getTotalWin() - this.getUnplayedBonusWin());

        super.onExit();
    }

    protected showWin(win: SymbolWinResult): Contract<void> {
        return new Sequence([
            () =>
                Contract.wrap(() => {
                    // Show winboxes
                    win.positions.forEach(pos => {
                        const box = Services.get(GraphicsService).createSprite("slotbonus_winbox");
                        this.winBoxes.push(box);
                        gameLayers.MatrixBackground.add(box);

                        const symbolPos = gameLayers.MatrixContent.getPosition(`symbol_${pos.x}_${pos.y}`);
                        box.setDualPosition(symbolPos);
                    });
                }),
            () => new Parallel([() => super.showWin(win), () => this.addWin(win)]),
            () =>
                Contract.wrap(() => {
                    this.winBoxes.forEach(box => box.destroy());
                    this.winBoxes = [];
                }),
        ]);
    }

    protected addWin(win: SymbolWinResult): Contract {
        return this.cancelGroup.tweenContract(
            new Tween(this)
                .to({ totalWinValue: this.totalWinValue + win.cashWon }, 750)
                .easing(Easing.Cubic.InOut)
                .onUpdate(() => this.updateWinText())
                .onStart(() => Services.get(SoundService).event(SoundEvent.winnings_countup))
                .onComplete(() => Services.get(SoundService).event(SoundEvent.winnings_countup_end))
                .onStop(() => Services.get(SoundService).event(SoundEvent.winnings_countup_end))
        );
    }

    protected updateWinText() {
        if (this.totalWinValue > 0) {
            const text = gameLayers.BonusUI.getText("total_win_value");
            text.text = Services.get(CurrencyService).format(this.totalWinValue);
        }
    }

    protected getTotalSlotWin(includeCurrentRecord: boolean): number {
        const gameplay = gameState.getCurrentGame();

        let win = 0;
        gameplay.getFamily(gameplay.getRootRecord()).forEach(record => {
            const isCurrent = record === gameplay.getCurrentRecord();
            if (record instanceof FreespinRecord && (includeCurrentRecord || !isCurrent)) {
                win += record.cashWon;
            }
        });

        return win;
    }

    protected getUnplayedBonusWin(): number {
        const currentRecord = gameState.getCurrentGame().getCurrentRecord();
        let unplayedBonusWin = 0;
        currentRecord.results.forEach(result => {
            if (result instanceof BonusResult && !result.played) {
                unplayedBonusWin += result.cashWon;
            }
        });
        return unplayedBonusWin;
    }
}
